import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, TextInput } from '@forecastify/ui';
import { SIGN_UP_CODE_LENGTH } from '@forecastify/shared';
import { apiUrl } from '../api';
import { useT, type MessageKey } from '../i18n';
import { SIGN_IN_PATH } from '../routes';
import { useSession } from '../session';
import { AuthField, AuthFrame } from './auth/auth-frame';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ `/sign-up` — self sign-up, flow A: an address, then the code mailed to it.│
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ `0203` · **NO PASSWORD HERE ANY MORE.** The first-run setup flow asks for the name, a picture
 * and the password in its step 1, which `Gate` shows the moment the code has signed somebody in —
 * so this screen is only what proves the address. The confirmation LINK it used to send is gone
 * too: following one confirmed the address in whatever tab the mail client opened, not in the one
 * waiting here.
 *
 * ⚠️ **The landing page will be the real entrance** (it is not built yet); until then this is
 * reached from the sign-in screen's own link, which is what makes the flow testable today.
 *
 * ⚠️ **An address that already has an account gets no code, and the screen cannot say so** —
 * `AuthService.signUp` answers identically either way, because a form that told strangers which
 * addresses are registered would be an oracle. The code step says it in general terms instead.
 */

const REASON: Readonly<Record<string, MessageKey>> = {
  validation_failed: 'signUp.invalidEmail',
  invalid_code: 'signUp.code.invalid',
  rate_limited: 'signUp.rateLimited',
  unavailable: 'signUp.unavailable',
};

export function SignUpScreen() {
  const t = useT();
  const session = useSession();

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [phase, setPhase] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [resent, setResent] = useState(false);

  /** Ask for a code — the first time, and again from the code step. */
  const requestCode = async (): Promise<boolean> => {
    const response = await fetch(apiUrl('/auth/sign-up'), {
      method: 'POST',
      /* `api.ts`: credentials always, so a cookie the API sets is never discarded at issue. */
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: email.trim() }),
    }).catch(() => null);
    if (response === null) {
      setFailure('unavailable');
      return false;
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: unknown };
      } | null;
      setFailure(typeof body?.error?.code === 'string' ? body.error.code : 'unknown');
      return false;
    }
    return true;
  };

  const submitEmail = (): void => {
    setBusy(true);
    setFailure(null);
    void requestCode().then((ok) => {
      setBusy(false);
      if (ok) setPhase('code');
    });
  };

  const submitCode = (): void => {
    setBusy(true);
    setFailure(null);
    setResent(false);
    /* On success the session is signed in and `Gate` replaces this screen with setup step 1. */
    void session.signInWithCode(email.trim(), code).then((result) => {
      setBusy(false);
      if (!result.ok) setFailure(result.code ?? 'unknown');
    });
  };

  const failureLine =
    failure === null ? null : (
      <p role="alert" className="text-destructive text-[12px]">
        {t(REASON[failure] ?? 'error.unknown')}
      </p>
    );

  const body =
    phase === 'email' ? (
      <form
        className="flex flex-col gap-3.5"
        onSubmit={(event) => {
          event.preventDefault();
          submitEmail();
        }}
      >
        <p className="text-muted-foreground text-[12.5px] leading-relaxed">{t('signUp.lede')}</p>
        <AuthField label={t('signUp.email')}>
          <TextInput
            type="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </AuthField>
        {failureLine}
        <Button type="submit" size="lg" className="w-full" disabled={busy || email.trim() === ''}>
          {busy ? t('signUp.working') : t('signUp.submit')}
        </Button>
        {/* ⚠️ A `<Link>`, not a button — a control that NAVIGATES is not a button. */}
        <Link
          to={SIGN_IN_PATH}
          className="text-muted-foreground hover:text-foreground text-center text-[11.5px] underline"
        >
          {t('signUp.haveAccount')}
        </Link>
      </form>
    ) : (
      <form
        className="flex flex-col gap-3.5"
        onSubmit={(event) => {
          event.preventDefault();
          submitCode();
        }}
      >
        <p role="status" className="text-[13px] leading-relaxed">
          {t('signUp.code.lede', { email: email.trim() })}
        </p>
        <AuthField label={t('signUp.code.label')}>
          <TextInput
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={SIGN_UP_CODE_LENGTH}
            className="text-center text-[18px] font-semibold tracking-[0.3em] tabular-nums"
            value={code}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, '').slice(0, SIGN_UP_CODE_LENGTH));
            }}
          />
        </AuthField>
        {failureLine}
        {resent ? (
          <p role="status" className="text-muted-foreground text-[12px]">
            {t('signUp.code.resent')}
          </p>
        ) : null}
        <Button
          type="submit"
          size="lg"
          className="w-full"
          disabled={busy || code.length !== SIGN_UP_CODE_LENGTH}
        >
          {busy ? t('signUp.working') : t('signUp.code.submit')}
        </Button>
        <div className="flex items-center justify-between gap-3 text-[11.5px]">
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground underline"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setFailure(null);
              setResent(false);
              void requestCode().then((ok) => {
                setBusy(false);
                setResent(ok);
              });
            }}
          >
            {t('signUp.code.resend')}
          </button>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground underline"
            onClick={() => {
              setPhase('email');
              setCode('');
              setFailure(null);
              setResent(false);
            }}
          >
            {t('signUp.code.changeEmail')}
          </button>
        </div>
        <p className="text-muted-foreground text-[11.5px] leading-relaxed">
          {t('signUp.code.existing')}{' '}
          <Link to={SIGN_IN_PATH} className="hover:text-foreground underline">
            {t('signUp.code.signIn')}
          </Link>
        </p>
      </form>
    );

  return (
    <AuthFrame
      screen="sign-up"
      title={phase === 'email' ? t('signUp.title') : t('signUp.code.title')}
    >
      {body}
    </AuthFrame>
  );
}
